import {
  CSSProperties,
  FC,
  Fragment,
  memo,
  ReactNode,
  PointerEvent as ReactPointerEvent,
  useEffect,
  useState,
} from 'react'

import { Document, Page, pdfjs } from 'react-pdf'
import styled from 'styled-components'

import { normalizeTranslationOverlayItems } from 'app/application_revamp/components/AttachmentViewerModal/translationLayout'
import type {
  IManualRedactionBoundingBox,
  ITranslationPreviewLine,
  TranslationSourceCoverTone,
} from 'app/application_revamp/components/AttachmentViewerModal/translationLayout'
import {
  Box,
  Button,
  Flex,
  Modal,
} from 'app/components/design-system-components'
import Badge from 'app/components/design-system-components/Badges'
import { AttachmentRounded } from 'app/components/design-system-components/icons/file'
import { PersonOutlineRounded } from 'app/components/design-system-components/icons/social'
import {
  GenericModal,
  GenericModalFooter,
} from 'app/components/design-system-components/modals/GenericModal'
import { fontWeight } from 'app/components/design-system-components/styles/variables'
import type {
  AnalysisDocumentBlock,
  AnalysisItem,
  DataType,
} from 'app/components/display/smartdocs/explainer'
import ExplainerWithHeading from 'app/components/display/smartdocs/explainerWithHeading'
import { normalizeSmartDocsAnalysisBlocks } from 'app/components/display/smartdocs/helpers'
import { StyledEllipse } from 'app/components/property/PropertySummary'
import theme from 'app/match/applicationReportPDF/assets/theme'
import * as dateTimeHelpers from 'app/utils/datetime/helpers'
import { formatPhoneNumber } from 'app/utils/phonenumber/helpers'
import { trimAndAddEllipsesPastSpecifiedLength } from 'app/utils/strings/helpers'

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`

interface IDocumentViewerAttachments {
  url: string
  sourceUrl?: string
  name: string
  typeOfDocument?: string
  description?: string
  guidID: string
  isAutoDeleted?: boolean
  fileAutoDeletedAt?: string
  redactionState?: string
  translationPreview?: ITranslationPreview | null
}

interface ITranslationPreview {
  isForeignLanguage: boolean
  detectedLanguage?: string
  confidence?: number
  lines?: ITranslationPreviewLine[]
}

interface ITranslationOverlayProps {
  lines: ITranslationPreviewLine[]
  page: number
}

interface IManualRedactionArea {
  page: number
  boundingBox: IManualRedactionBoundingBox
}

interface IManualRedactionSavePayload {
  documentGuid: string
  areas: IManualRedactionArea[]
}

interface IDocumentViewerProps {
  toggleDocumentsViewer: () => void
  currentApplicantFullName?: string
  currentApplicantEmail?: string
  currentApplicantPhone?: string
  documentViewerAttachments: IDocumentViewerAttachments[]
  documentsViewerGUID: string
  smartDocsAnalysis?: DataType
  enableManualRedaction?: boolean
  hidePrintButton?: boolean
  loadTranslationPreview?: (
    sourceUrl: string,
  ) => Promise<ITranslationPreview | null>
  onSaveManualRedactions?: (
    payload: IManualRedactionSavePayload,
  ) => Promise<void> | void
  onClickConfirmOKForAnalysis: (analysisGUID: string) => void
  onClickUndoForAnalysis: (
    analysisGUID: string,
    confirmationGUID: string,
  ) => void
}

interface IDraftManualRedaction {
  startX: number
  startY: number
  endX: number
  endY: number
}

interface IManualRedactionOverlayProps {
  isDrawingEnabled: boolean
  page: number
  savedAreas: IManualRedactionArea[]
  onAreaCreated: (area: IManualRedactionArea) => void
}

interface IRenderedPdfPageProps {
  isPresentationOverlayEnabled: boolean
  page: number
}

interface IManualRedactionPageProps {
  isDrawingEnabled: boolean
  isManualRedactionEnabled: boolean
  isTranslationMode: boolean
  onAreaCreated: (area: IManualRedactionArea) => void
  page: number
  savedAreas: IManualRedactionArea[]
  translationPreviewLines: ITranslationPreviewLine[]
}

const bankStatementRedactionStateReady = 'ready'
const bankStatementRedactedUrlFragment = 'bank-redacted'

const StyledPdfPageContainer = styled(Box)`
  position: relative;
  display: inline-block;
  line-height: 0;

  &:not(:first-child) {
    margin-top: ${theme.space[4]}px;
  }
`

const StyledDocument = styled(Box)`
  border: 1px solid ${theme.colors.gray400};
`

const StyledManualRedactionToolbar = styled(Flex)`
  gap: ${theme.space[2]}px;
  margin-top: ${theme.space[4]}px;
  flex-direction: column;
`

const StyledManualRedactionCanvas = styled(Box)<{
  $isDrawingEnabled: boolean
}>`
  position: absolute;
  inset: 0;
  cursor: ${({ $isDrawingEnabled }) =>
    $isDrawingEnabled ? 'crosshair' : 'default'};
  pointer-events: ${({ $isDrawingEnabled }) =>
    $isDrawingEnabled ? 'auto' : 'none'};
  user-select: none;
  touch-action: none;
`

const StyledManualRedactionButtons = styled(Flex)`
  gap: ${theme.space[2]}px;
  align-items: center;
  flex-wrap: wrap;
`

const StyledManualRedactionHelperText = styled(Box)`
  color: ${theme.colors.gray600};
  font-size: ${theme.fontSizes.pSmall12}px;
`

const StyledManualRedactionStatusRow = styled(Box)<{
  $isVisible: boolean
}>`
  display: ${({ $isVisible }) => ($isVisible ? 'block' : 'none')};
`

const StyledManualRedactionBox = styled(Box)<{
  $isDraft?: boolean
  $isPreview?: boolean
}>`
  position: absolute;
  background: ${({ $isDraft, $isPreview }) => {
    if ($isDraft) {
      return 'rgba(0, 0, 0, 0.18)'
    }
    if ($isPreview) {
      return 'rgba(0, 0, 0, 0.38)'
    }
    return 'rgba(0, 0, 0, 0.85)'
  }};
  border: ${({ $isDraft, $isPreview }) => {
    if ($isDraft) {
      return '2px dashed rgba(0, 0, 0, 0.75)'
    }
    if ($isPreview) {
      return '2px solid rgba(0, 0, 0, 0.8)'
    }
    return 'none'
  }};
`

const StyledTranslationOverlay = styled(Box)`
  position: absolute;
  inset: 0;
  pointer-events: none;
`

const StyledTranslationPlaceholder = styled(Box)`
  position: absolute;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: ${theme.space[1]}px;
`

const StyledTranslationSourceCover = styled(Box)<{
  $backgroundColor: string
}>`
  position: absolute;
  z-index: 1;
  background: ${({ $backgroundColor }) => $backgroundColor};
  border-radius: 3px;
  box-sizing: border-box;
`

const StyledTranslationPlaceholderCard = styled(Box)`
  width: 100%;
  min-height: 100%;
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.gray300};
  border-radius: 6px;
  text-align: center;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
  overflow: visible;
`

const StyledTranslationPlaceholderLabel = styled(Box)`
  color: ${theme.colors.gray900};
  font-weight: ${fontWeight[5]};
  display: flex;
  width: 100%;
  min-height: 100%;
  align-items: center;
  justify-content: center;
  white-space: pre-line;
  overflow-wrap: anywhere;
  word-break: normal;
`

export const clampNormalizedCoordinate = (value: number) =>
  Math.max(0, Math.min(1, value))

const manualRedactionPadding = 0.008
const translationOverlayHorizontalPadding = 0.035
const translationOverlayVerticalPadding = 0.008
const fixedTranslationFontSize = 8
const minimumTranslationFontSize = 6
const fixedTranslationPadding = {
  paddingInline: 4,
  paddingBlock: 2,
}
const approximateRenderedPdfWidthPx = 900
const averageTranslationCharacterWidthFactor = 0.58
const translationMaxWidth = 0.42
const translationRedBandColor = theme.colors.primary600

const expandBoundingBox = (boundingBox: IManualRedactionBoundingBox) => {
  const left = clampNormalizedCoordinate(
    boundingBox.left - translationOverlayHorizontalPadding,
  )
  const top = clampNormalizedCoordinate(
    boundingBox.top - translationOverlayVerticalPadding,
  )
  const right = clampNormalizedCoordinate(
    boundingBox.left + boundingBox.width + translationOverlayHorizontalPadding,
  )
  const bottom = clampNormalizedCoordinate(
    boundingBox.top + boundingBox.height + translationOverlayVerticalPadding,
  )

  return {
    left,
    top,
    width: right - left,
    height: bottom - top,
  }
}

const resolveTranslationPlacement = ({
  boundingBox,
  fontSize,
  horizontalPadding = translationOverlayHorizontalPadding,
  translatedText,
}: {
  boundingBox: IManualRedactionBoundingBox
  fontSize: number
  horizontalPadding?: number
  translatedText: string
}): CSSProperties => {
  const paddedBoundingBox = expandBoundingBox({
    ...boundingBox,
    left:
      boundingBox.left +
      translationOverlayHorizontalPadding -
      horizontalPadding,
    width:
      boundingBox.width -
      (translationOverlayHorizontalPadding - horizontalPadding) * 2,
  })
  const longestTranslatedLine = translatedText
    .split(/\r?\n/)
    .map((part) => part.trim())
    .reduce(
      (longest, part) => (part.length > longest.length ? part : longest),
      '',
    )
  const estimatedTranslatedWidth =
    (longestTranslatedLine.length *
      fontSize *
      averageTranslationCharacterWidthFactor +
      fixedTranslationPadding.paddingInline * 2 +
      (theme.space[1] ?? 0) * 2) /
    approximateRenderedPdfWidthPx
  const right = clampNormalizedCoordinate(
    paddedBoundingBox.left + paddedBoundingBox.width,
  )
  // Source label past the page midline — anchor overlay to the right edge to prevent overflow.
  const RIGHT_ALIGN_THRESHOLD = 0.55
  const isRightAligned = paddedBoundingBox.left > RIGHT_ALIGN_THRESHOLD
  const availableWidth = isRightAligned
    ? right
    : clampNormalizedCoordinate(1 - paddedBoundingBox.left)
  const width = Math.min(
    Math.max(paddedBoundingBox.width, estimatedTranslatedWidth),
    Math.min(translationMaxWidth, availableWidth),
  )

  return {
    top: `${paddedBoundingBox.top * 100}%`,
    width: `${width * 100}%`,
    minHeight: `${paddedBoundingBox.height * 100}%`,
    ...(isRightAligned
      ? { right: `${(1 - right) * 100}%` }
      : { left: `${paddedBoundingBox.left * 100}%` }),
  }
}

const resolveSourceCoverPlacement = (
  boundingBox: IManualRedactionBoundingBox,
): CSSProperties => ({
  left: `${boundingBox.left * 100}%`,
  top: `${boundingBox.top * 100}%`,
  width: `${boundingBox.width * 100}%`,
  height: `${boundingBox.height * 100}%`,
})

const resolveSourceCoverBackgroundColor = (tone: TranslationSourceCoverTone) =>
  tone === 'primaryBand' ? translationRedBandColor : theme.colors.white

const resolveTranslationFontSize = (
  boundingBox: IManualRedactionBoundingBox,
  translatedText: string,
) => {
  const normalizedText = translatedText.trim()
  if (!normalizedText) {
    return fixedTranslationFontSize
  }

  const availableWidthPx = Math.max(
    boundingBox.width * approximateRenderedPdfWidthPx -
      fixedTranslationPadding.paddingInline * 2,
    1,
  )
  const estimatedSingleLineFontSize =
    availableWidthPx /
    (normalizedText.length * averageTranslationCharacterWidthFactor)

  return Math.max(
    minimumTranslationFontSize,
    Math.min(fixedTranslationFontSize, estimatedSingleLineFontSize),
  )
}

export const buildManualRedactionArea = (
  page: number,
  draft: IDraftManualRedaction | null,
): IManualRedactionArea | null => {
  if (!draft) {
    return null
  }

  const left = clampNormalizedCoordinate(Math.min(draft.startX, draft.endX))
  const top = clampNormalizedCoordinate(Math.min(draft.startY, draft.endY))
  const width = clampNormalizedCoordinate(Math.abs(draft.endX - draft.startX))
  const height = clampNormalizedCoordinate(Math.abs(draft.endY - draft.startY))

  if (width < 0.01 || height < 0.01) {
    return null
  }

  const paddedLeft = clampNormalizedCoordinate(left - manualRedactionPadding)
  const paddedTop = clampNormalizedCoordinate(top - manualRedactionPadding)
  const paddedRight = clampNormalizedCoordinate(
    left + width + manualRedactionPadding,
  )
  const paddedBottom = clampNormalizedCoordinate(
    top + height + manualRedactionPadding,
  )

  return {
    page,
    boundingBox: {
      left: paddedLeft,
      top: paddedTop,
      width: paddedRight - paddedLeft,
      height: paddedBottom - paddedTop,
    },
  }
}

export const resolveNormalizedCoordinates = (
  event: Pick<ReactPointerEvent<HTMLDivElement>, 'clientX' | 'clientY'>,
  target: HTMLDivElement,
) => {
  const rect = target.getBoundingClientRect()
  if (!rect.width || !rect.height) {
    return null
  }

  return {
    x: clampNormalizedCoordinate((event.clientX - rect.left) / rect.width),
    y: clampNormalizedCoordinate((event.clientY - rect.top) / rect.height),
  }
}

const ManualRedactionOverlay: FC<IManualRedactionOverlayProps> = ({
  isDrawingEnabled,
  page,
  savedAreas,
  onAreaCreated,
}) => {
  const [draftRedaction, setDraftRedaction] =
    useState<IDraftManualRedaction | null>(null)

  useEffect(() => {
    if (!isDrawingEnabled) {
      setDraftRedaction(null)
    }
  }, [isDrawingEnabled])

  const releaseCapturedPointer = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture?.(event.pointerId)
    }
  }

  const startDrawing = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.isPrimary === false || event.button !== 0) {
      return
    }

    event.preventDefault()
    event.currentTarget.setPointerCapture?.(event.pointerId)

    const coordinates = resolveNormalizedCoordinates(event, event.currentTarget)
    if (!coordinates) {
      return
    }

    setDraftRedaction({
      startX: coordinates.x,
      startY: coordinates.y,
      endX: coordinates.x,
      endY: coordinates.y,
    })
  }

  const updateDrawing = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!draftRedaction) {
      return
    }

    event.preventDefault()
    const coordinates = resolveNormalizedCoordinates(event, event.currentTarget)
    if (!coordinates) {
      return
    }

    setDraftRedaction({
      ...draftRedaction,
      endX: coordinates.x,
      endY: coordinates.y,
    })
  }

  const finishDrawing = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!draftRedaction) {
      return
    }

    event.preventDefault()
    releaseCapturedPointer(event)
    const coordinates = resolveNormalizedCoordinates(event, event.currentTarget)
    const finalizedDraft = coordinates
      ? {
          ...draftRedaction,
          endX: coordinates.x,
          endY: coordinates.y,
        }
      : draftRedaction
    const finalizedArea = buildManualRedactionArea(page, finalizedDraft)

    if (finalizedArea) {
      onAreaCreated(finalizedArea)
    }

    setDraftRedaction(null)
  }

  const cancelDrawing = (event?: ReactPointerEvent<HTMLDivElement>) => {
    if (event) {
      releaseCapturedPointer(event)
    }
    setDraftRedaction(null)
  }

  const draftArea = buildManualRedactionArea(page, draftRedaction)

  return (
    <StyledManualRedactionCanvas
      $isDrawingEnabled={isDrawingEnabled}
      data-manual-redaction-overlay={`page-${page}`}
      onPointerDown={startDrawing}
      onPointerMove={updateDrawing}
      onPointerUp={finishDrawing}
      onPointerCancel={cancelDrawing}
      onPointerLeave={cancelDrawing}
    >
      {savedAreas.map((area) => (
        <StyledManualRedactionBox
          $isPreview
          key={`${page}-${area.boundingBox.left}-${area.boundingBox.top}-${area.boundingBox.width}-${area.boundingBox.height}`}
          style={{
            left: `${area.boundingBox.left * 100}%`,
            top: `${area.boundingBox.top * 100}%`,
            width: `${area.boundingBox.width * 100}%`,
            height: `${area.boundingBox.height * 100}%`,
          }}
        />
      ))}
      {draftArea && (
        <StyledManualRedactionBox
          $isDraft
          data-manual-redaction-draft="true"
          style={{
            left: `${draftArea.boundingBox.left * 100}%`,
            top: `${draftArea.boundingBox.top * 100}%`,
            width: `${draftArea.boundingBox.width * 100}%`,
            height: `${draftArea.boundingBox.height * 100}%`,
          }}
        />
      )}
    </StyledManualRedactionCanvas>
  )
}

const TranslationOverlay: FC<ITranslationOverlayProps> = ({ lines, page }) => {
  const overlayItems = normalizeTranslationOverlayItems(
    lines.filter((line) => line.page === page),
  ).slice(0, 24)

  if (!overlayItems.length) {
    return null
  }

  return (
    <StyledTranslationOverlay data-translation-overlay="true">
      {overlayItems.map((item) => {
        const fontSizeBoundingBox = expandBoundingBox(item.labelBoundingBox)
        const translationFontSize = resolveTranslationFontSize(
          fontSizeBoundingBox,
          item.line.translatedText,
        )

        return (
          <Fragment
            key={`${page}-${item.line.coordinateIndex}-${item.line.originalText}`}
          >
            <StyledTranslationSourceCover
              $backgroundColor={resolveSourceCoverBackgroundColor(
                item.sourceCoverTone,
              )}
              style={resolveSourceCoverPlacement(item.sourceCoverBoundingBox)}
            />
            <StyledTranslationPlaceholder
              style={resolveTranslationPlacement({
                boundingBox: item.labelBoundingBox,
                fontSize: translationFontSize,
                translatedText: item.line.translatedText,
                ...(item.horizontalPadding !== undefined
                  ? { horizontalPadding: item.horizontalPadding }
                  : {}),
              })}
            >
              <StyledTranslationPlaceholderCard
                style={{
                  padding: `${fixedTranslationPadding.paddingBlock}px ${fixedTranslationPadding.paddingInline}px`,
                }}
              >
                <StyledTranslationPlaceholderLabel
                  style={{
                    fontSize: `${translationFontSize}px`,
                    lineHeight: 1.05,
                  }}
                >
                  {item.line.translatedText}
                </StyledTranslationPlaceholderLabel>
              </StyledTranslationPlaceholderCard>
            </StyledTranslationPlaceholder>
          </Fragment>
        )
      })}
    </StyledTranslationOverlay>
  )
}

const RenderedPdfPage = memo(
  ({ isPresentationOverlayEnabled, page }: IRenderedPdfPageProps) => (
    <StyledDocument>
      <Page
        pageNumber={page}
        renderAnnotationLayer={!isPresentationOverlayEnabled}
        renderTextLayer={!isPresentationOverlayEnabled}
      />
    </StyledDocument>
  ),
)

const ManualRedactionPage: FC<IManualRedactionPageProps> = ({
  isDrawingEnabled,
  isManualRedactionEnabled,
  isTranslationMode,
  onAreaCreated,
  page,
  savedAreas,
  translationPreviewLines,
}) => (
  <StyledPdfPageContainer>
    <RenderedPdfPage
      isPresentationOverlayEnabled={
        isManualRedactionEnabled || isTranslationMode
      }
      page={page}
    />
    {isTranslationMode && (
      <TranslationOverlay lines={translationPreviewLines} page={page} />
    )}
    {isManualRedactionEnabled && (
      <ManualRedactionOverlay
        isDrawingEnabled={isDrawingEnabled}
        page={page}
        savedAreas={savedAreas}
        onAreaCreated={onAreaCreated}
      />
    )}
  </StyledPdfPageContainer>
)

const DocumentsViewer: FC<IDocumentViewerProps> = ({
  currentApplicantFullName = '',
  currentApplicantEmail = '',
  currentApplicantPhone = '',
  toggleDocumentsViewer,
  documentViewerAttachments,
  documentsViewerGUID,
  smartDocsAnalysis,
  enableManualRedaction = false,
  hidePrintButton = false,
  loadTranslationPreview,
  onSaveManualRedactions,
  onClickConfirmOKForAnalysis,
  onClickUndoForAnalysis,
}) => {
  const totalAttachments = documentViewerAttachments?.length
  const [currentNumAttachment, setCurrentNumAttachment] = useState(0)
  const [
    smartDocsAnalysisForCurrentAttachment,
    setSmartDocsAnalysisForCurrentAttachment,
  ] = useState<DataType | undefined>()
  const [numPages, setNumPages] = useState<number | null>(null)
  const [isManualRedactionMode, setIsManualRedactionMode] = useState(false)
  const [isSavingManualRedactions, setIsSavingManualRedactions] =
    useState(false)
  const [isSaveConfirmationOpen, setIsSaveConfirmationOpen] = useState(false)
  const [isTranslationMode, setIsTranslationMode] = useState(false)
  const [manualRedactionsByDocument, setManualRedactionsByDocument] = useState<
    Record<string, IManualRedactionArea[]>
  >({})
  const [translationPreviewByDocument, setTranslationPreviewByDocument] =
    useState<Record<string, ITranslationPreview | null>>({})
  const [
    translationPreviewSourceUrlByDocument,
    setTranslationPreviewSourceUrlByDocument,
  ] = useState<Record<string, string>>({})
  useEffect(() => {
    if (!documentsViewerGUID) {
      return
    }

    const selectedAttachmentIndex = documentViewerAttachments.findIndex(
      (documentViewerAttachment) =>
        documentViewerAttachment.guidID === documentsViewerGUID,
    )

    if (selectedAttachmentIndex >= 0) {
      setCurrentNumAttachment(selectedAttachmentIndex)
    }
  }, [documentViewerAttachments, documentsViewerGUID])

  const currentAttachment = documentViewerAttachments?.[currentNumAttachment]
  const currentAttachmentGuid = currentAttachment?.guidID || ''
  const currentAttachmentSourceUrl =
    currentAttachment?.sourceUrl || currentAttachment?.url || ''
  const preloadedTranslationPreview =
    currentAttachment?.translationPreview || null

  useEffect(() => {
    let formedAnalysisData: DataType | undefined
    const analysisForCurrentAttachment =
      smartDocsAnalysis?.analysis[currentAttachmentGuid]
    const analysisBlocks = normalizeSmartDocsAnalysisBlocks<
      AnalysisItem,
      AnalysisDocumentBlock
    >(analysisForCurrentAttachment).filter((block) => block.fields.length > 0)

    if (analysisBlocks.length > 0) {
      const normalizedAnalysisForCurrentAttachment = Array.isArray(
        analysisForCurrentAttachment,
      )
        ? analysisBlocks
        : analysisBlocks[0]!

      formedAnalysisData = {
        analysis: {
          [currentAttachmentGuid]: normalizedAnalysisForCurrentAttachment,
        },
      }
    }

    setSmartDocsAnalysisForCurrentAttachment(formedAnalysisData)
  }, [currentAttachmentGuid, smartDocsAnalysis])

  useEffect(() => {
    setIsManualRedactionMode(false)
    setIsTranslationMode(false)
    setNumPages(null)
    setIsSaveConfirmationOpen(false)
  }, [currentAttachmentGuid])

  const attachmentNameParts = (currentAttachment?.name || '').split('.')
  const typeOfAttachment =
    attachmentNameParts[attachmentNameParts.length - 1] || ''
  const attachmentFileName =
    attachmentNameParts.slice(0, -1).join('.') || currentAttachment?.name || ''
  const attachmentFileNameToDisplay = trimAndAddEllipsesPastSpecifiedLength(
    attachmentFileName,
    30,
  )
  const currentManualRedactions =
    manualRedactionsByDocument[currentAttachmentGuid] || []
  const currentAttachmentHasRedactedPDF = Boolean(
    currentAttachment?.url?.includes(bankStatementRedactedUrlFragment) ||
      currentAttachment?.redactionState === bankStatementRedactionStateReady,
  )
  const isManualRedactionEnabledForCurrentAttachment = Boolean(
    enableManualRedaction &&
      currentAttachmentGuid &&
      currentAttachment?.url &&
      typeOfAttachment?.toLowerCase() === 'pdf' &&
      currentAttachmentHasRedactedPDF,
  )
  const currentTranslationPreview =
    preloadedTranslationPreview ||
    translationPreviewByDocument[currentAttachmentGuid] ||
    null
  const currentTranslationPreviewLines = currentTranslationPreview?.lines || []
  const hasTranslationPreview = Boolean(
    currentTranslationPreview?.isForeignLanguage,
  )
  const shouldHidePrintButton = Boolean(
    hidePrintButton && isManualRedactionEnabledForCurrentAttachment,
  )

  useEffect(() => {
    let isCancelled = false

    if (
      loadTranslationPreview &&
      currentAttachmentGuid &&
      currentAttachmentSourceUrl &&
      typeOfAttachment?.toLowerCase() === 'pdf' &&
      currentAttachmentHasRedactedPDF &&
      !preloadedTranslationPreview &&
      !(
        Object.prototype.hasOwnProperty.call(
          translationPreviewByDocument,
          currentAttachmentGuid,
        ) &&
        translationPreviewSourceUrlByDocument[currentAttachmentGuid] ===
          currentAttachmentSourceUrl
      )
    ) {
      loadTranslationPreview(currentAttachmentSourceUrl).then((preview) => {
        if (!isCancelled) {
          setTranslationPreviewByDocument((previous) => ({
            ...previous,
            [currentAttachmentGuid]: preview,
          }))
          setTranslationPreviewSourceUrlByDocument((previous) => ({
            ...previous,
            [currentAttachmentGuid]: currentAttachmentSourceUrl,
          }))
        }
      })
    }

    // skipcq: JS-0045 — React useEffect cleanup destructor; EffectCallback is typed () => void | Destructor
    return () => {
      isCancelled = true
    }
  }, [
    currentAttachmentGuid,
    currentAttachmentHasRedactedPDF,
    currentAttachmentSourceUrl,
    loadTranslationPreview,
    preloadedTranslationPreview,
    translationPreviewByDocument,
    translationPreviewSourceUrlByDocument,
    typeOfAttachment,
  ])

  const onDocumentLoadSuccess = ({
    numPages: nextNumPages,
  }: {
    numPages: number
  }) => {
    setNumPages(nextNumPages)
  }

  const addManualRedactionArea = (area: IManualRedactionArea) => {
    setManualRedactionsByDocument((previousRedactions) => ({
      ...previousRedactions,
      [currentAttachmentGuid]: [
        ...(previousRedactions[currentAttachmentGuid] || []),
        area,
      ],
    }))
  }

  const saveManualRedactions = async () => {
    if (
      !onSaveManualRedactions ||
      !currentAttachmentGuid ||
      currentManualRedactions.length === 0
    ) {
      return
    }

    setIsSavingManualRedactions(true)
    try {
      await onSaveManualRedactions({
        documentGuid: currentAttachmentGuid,
        areas: currentManualRedactions,
      })
      setManualRedactionsByDocument((previousRedactions) => ({
        ...previousRedactions,
        [currentAttachmentGuid]: [],
      }))
      setIsManualRedactionMode(false)
      setIsSaveConfirmationOpen(false)
    } catch {
      // Error is handled by the caller (e.g. snugNotifier.error in container)
    } finally {
      setIsSavingManualRedactions(false)
    }
  }

  const hasDraftRedactions =
    currentManualRedactions.length > 0 && !isSavingManualRedactions
  const saveButtonVariant = hasDraftRedactions
    ? 'solidSuccessStrong'
    : 'outlineSuccess'
  const translationButtonLabel = isTranslationMode
    ? 'View original'
    : 'View English'
  const renderManualRedactionToolbar =
    isManualRedactionEnabledForCurrentAttachment ? (
      <StyledManualRedactionToolbar>
        <StyledManualRedactionHelperText>
          {isTranslationMode
            ? 'Review English translations before saving changes.'
            : 'Hide transaction details by drawing boxes over them before you save.'}
        </StyledManualRedactionHelperText>
        <StyledManualRedactionButtons>
          <Button
            sizeVariant="extraSmall"
            variant="outlineSuccess"
            aria-pressed={isManualRedactionMode}
            data-manual-redaction-mode={
              isManualRedactionMode ? 'active' : 'inactive'
            }
            onClick={() => {
              if (isTranslationMode) {
                setIsTranslationMode(false)
                setIsManualRedactionMode(true)
                return
              }

              setIsManualRedactionMode(!isManualRedactionMode)
            }}
          >
            Hide transaction details
          </Button>
          <Button
            sizeVariant="extraSmall"
            variant={saveButtonVariant}
            disabled={
              currentManualRedactions.length === 0 || isSavingManualRedactions
            }
            onClick={() => {
              setIsSaveConfirmationOpen(true)
            }}
          >
            {isSavingManualRedactions ? 'Saving...' : 'Save changes'}
          </Button>
          {hasTranslationPreview && (
            <Button
              sizeVariant="extraSmall"
              variant={
                isTranslationMode ? 'solidSuccessStrong' : 'outlineSuccess'
              }
              onClick={() => {
                if (!isTranslationMode) {
                  setIsManualRedactionMode(false)
                }
                setIsTranslationMode(!isTranslationMode)
              }}
            >
              {translationButtonLabel}
            </Button>
          )}
        </StyledManualRedactionButtons>
        <StyledManualRedactionStatusRow $isVisible={isManualRedactionMode}>
          <Badge
            text="Draw boxes over transaction details on the document."
            variant="success"
          />
        </StyledManualRedactionStatusRow>
      </StyledManualRedactionToolbar>
    ) : null

  let attachmentRender: ReactNode = <Box></Box>
  if (currentAttachment?.isAutoDeleted) {
    attachmentRender = (
      <Box mt={10} textAlign="center">
        <Box color={theme.colors.primary300}>
          This attachment has been auto-deleted on{' '}
          {dateTimeHelpers.formatStdDateStamp(
            currentAttachment?.fileAutoDeletedAt,
          )}
          .
        </Box>
      </Box>
    )
  } else if (typeOfAttachment?.toLowerCase() === 'pdf') {
    attachmentRender = currentAttachment?.url ? (
      <Document
        file={currentAttachment?.url}
        onLoadSuccess={onDocumentLoadSuccess}
      >
        {Array.from({ length: numPages || 0 }, (_, index) => {
          const page = index + 1
          const savedRedactionsForPage = currentManualRedactions.filter(
            (area) => area.page === page,
          )

          return (
            <ManualRedactionPage
              key={page}
              isDrawingEnabled={isManualRedactionMode && !isTranslationMode}
              isManualRedactionEnabled={
                isManualRedactionEnabledForCurrentAttachment &&
                !isTranslationMode
              }
              isTranslationMode={isTranslationMode}
              onAreaCreated={addManualRedactionArea}
              page={page}
              savedAreas={savedRedactionsForPage}
              translationPreviewLines={currentTranslationPreviewLines}
            />
          )
        })}
      </Document>
    ) : null
  } else {
    attachmentRender = (
      <StyledDocument>
        <img
          src={currentAttachment?.url || ''}
          alt="attachment rendered"
          width="100%"
        />
      </StyledDocument>
    )
  }

  const applicantDetails = (
    <Flex alignItems="center">
      <PersonOutlineRounded color={theme.colors.gray500} />{' '}
      <Box color={theme.colors.gray800} ml={2} fontWeight={fontWeight[4]}>
        {currentApplicantFullName}
      </Box>
      <Box mr={2} ml={2} fontSize={theme.fontSizes.pRegular14}>
        <a href={`tel:${currentApplicantPhone}`}>
          {formatPhoneNumber(currentApplicantPhone)}
        </a>
      </Box>
      <StyledEllipse />
      <Box ml={2} fontSize={theme.fontSizes.pRegular14}>
        <a href={`mailto:${currentApplicantEmail}`}>{currentApplicantEmail}</a>
      </Box>
    </Flex>
  )
  const showApplicantDetails = Boolean(
    currentApplicantFullName || currentApplicantEmail || currentApplicantPhone,
  )
  const modalBody = (
    <Box
      width="100%"
      data-document-viewer-modal="true"
      data-document-viewer-current-url={currentAttachment?.url || ''}
      data-document-viewer-current-name={currentAttachment?.name || ''}
    >
      <Flex justifyContent="space-between">
        <Box>
          {showApplicantDetails && <Box mb={5}>{applicantDetails}</Box>}
          <Flex alignItems="center">
            <AttachmentRounded color={theme.colors.gray800} />
            <Flex ml={2} alignItems="center">
              <Box
                color={theme.colors.skyBlue}
                onClick={() => window.open(currentAttachment?.url)}
                style={{ cursor: 'pointer' }}
              >
                {attachmentFileNameToDisplay}.{typeOfAttachment}
              </Box>
              {currentAttachment?.typeOfDocument && (
                <Box ml={2}>({currentAttachment?.typeOfDocument})</Box>
              )}
            </Flex>
          </Flex>
          {currentAttachment?.description && (
            <Box
              mt={2}
              ml={7}
              color={theme.colors.gray500}
              fontSize={theme.fontSizes.pSmall12}
            >
              {currentAttachment.description}
            </Box>
          )}
        </Box>
        {!shouldHidePrintButton && (
          <Box textAlign="right">
            <Button
              sizeVariant="extraSmall"
              variant="outlineSuccess"
              onClick={() => {
                window.open(currentAttachment?.url)
              }}
            >
              Print
            </Button>
          </Box>
        )}
      </Flex>
      {renderManualRedactionToolbar}
      <ExplainerWithHeading
        isCopilot={false}
        isRenterSide={false}
        data={smartDocsAnalysisForCurrentAttachment || undefined}
        onClickConfirmOKForAnalysis={onClickConfirmOKForAnalysis}
        onClickUndoForAnalysis={onClickUndoForAnalysis}
        withLearnMore
      />
      <Box mt={isManualRedactionEnabledForCurrentAttachment ? 2 : 5}>
        {attachmentRender}
      </Box>
    </Box>
  )

  const nextAttachment = () => {
    if (currentNumAttachment < totalAttachments - 1) {
      setCurrentNumAttachment(currentNumAttachment + 1)
    }
  }

  const prevAttachment = () => {
    if (currentNumAttachment > 0) {
      setCurrentNumAttachment(currentNumAttachment - 1)
    }
  }

  const showPrimaryButton = currentNumAttachment < totalAttachments - 1
  const showSecondaryButton = currentNumAttachment > 0
  const useCustomModalFooter = isManualRedactionEnabledForCurrentAttachment

  const closeSaveConfirmation = () => {
    if (!isSavingManualRedactions) {
      setIsSaveConfirmationOpen(false)
    }
  }

  const renderSaveConfirmationModal = () => {
    if (!isSaveConfirmationOpen) {
      return null
    }
    return (
      <GenericModal
        title="Save changes?"
        onDismiss={closeSaveConfirmation}
        footerContainerProps={{ mt: `${theme.space[1]}px` }}
        modalFooter={
          <GenericModalFooter
            primaryBtnConfig={{
              title: isSavingManualRedactions ? 'Saving...' : 'Save changes',
              onClick: saveManualRedactions,
              props: {
                disabled: isSavingManualRedactions,
                loading: isSavingManualRedactions,
                disableWhenLoading: true,
              },
            }}
            secondaryBtnConfig={{
              title: 'Go back',
              onClick: closeSaveConfirmation,
              props: {
                disabled: isSavingManualRedactions,
              },
            }}
          />
        }
      >
        <Box
          data-manual-redaction-confirm-modal="true"
          color={theme.colors.gray700}
        >
          After you save, these changes can&apos;t be undone. Please review the
          document carefully before continuing. If something is incorrect, it
          may delay your application.
        </Box>
      </GenericModal>
    )
  }

  return (
    <Box>
      <Modal
        modalHeading="Attachments"
        modalBody={modalBody}
        modalFooter={
          useCustomModalFooter ? undefined : (
            <Box mt={2}>
              {currentNumAttachment + 1} of {totalAttachments} attachments
            </Box>
          )
        }
        customModalFooter={useCustomModalFooter}
        primaryLabel={useCustomModalFooter ? undefined : 'Next'}
        primaryAction={useCustomModalFooter ? undefined : nextAttachment}
        secondaryLabel={useCustomModalFooter ? undefined : 'Previous'}
        secondaryAction={useCustomModalFooter ? undefined : prevAttachment}
        primaryButtonDisabled={!showPrimaryButton}
        secondaryButtonDisabled={!showSecondaryButton}
        toggleModal={toggleDocumentsViewer}
        takeIncreasedWidth
        contentClass="min-height-78vh"
      />
      {renderSaveConfirmationModal()}
    </Box>
  )
}

export default DocumentsViewer
